import CommonLayout from 'layouts/DashboardLayout';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Checkbox, Grid, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { useNavigate } from 'react-router-dom';
import MDButton from 'components/MDButton';
import { formatDate } from 'utilities/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { openSnackbar } from '../../redux/slices/common';
import { CloseRounded } from '@mui/icons-material';
import StatusText from 'common/component/StatusText';
import SearchIcon from 'common/customIcons/search';
import { deleteBulkRole, deleteUser, fetchRoleList } from '../../redux/slices/roleAndPermissions';

export default function Users({ canView, canCreate, canEdit, canDelete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageRef = useRef(0);
  const searchTermRef = useRef('');
  const mainGridApiRef = useRef('');
  const gridRefApi = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { roles = {}, isLoading: loading = false } = useSelector(state => state.roleAndPermissions);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);

  const deleteHandler = (promotionId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteUser(promotionId)).unwrap().then((res) => {
      showResponseMessage(res?.message, 'success');
      dispatch(closeCustomModalDialog());
      pageRef.current = 0;
      const dataSource = {
        getRows
      };
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption('datasource', dataSource);
    }).catch((err) => {
      dispatch(setModalLoader(false));
      showResponseMessage(err?.message, 'error');
    });
  };

  const showResponseMessage = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };
  // const selectAllHandler = (e) => {
  //   if (e.target.checked) {
  //     // Select only non–Super Admin rows
  //     mainGridApiRef.current.forEachNode((node) => {
  //       const isSuperAdmin = node?.data?.isSuperAdmin;
  //       node.setSelected(!isSuperAdmin);
  //     });
  //   } else {
  //     // Deselect all
  //     mainGridApiRef.current.forEachNode((node) => {
  //       node.setSelected(false);
  //     });
  //   }
  // };
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const selectAllHandler = (e) => {
    const isChecked = e.target.checked;
    setSelectAllChecked(isChecked);

    if (isChecked) {
      // Select only non–Super Admin rows
      mainGridApiRef.current.forEachNode((node) => {
        const isSuperAdmin = node?.data?.isSuperAdmin;
        node.setSelected(!isSuperAdmin);
      });
    } else {
      // Deselect all
      mainGridApiRef.current.forEachNode((node) => {
        node.setSelected(false);
      });
    }
  };
  const myComponent = (props) => {
    return <>
      <Stack direction='row' gap={2}>
        <Checkbox
          size='medium'
          sx={{ margin: '0px -6px' }}
          checked={selectAllChecked}
          onChange={selectAllHandler}
        />
        <Typography variant='subtitle1' sx={{ fontSize: '12px', color: '#656575', lineHeight: 16, fontWeight: 400 }}>{props?.displayName}</Typography>
      </Stack>
    </>;
  };

  const columnDefs = [
    {
      headerName: 'Full Name',
      checkboxSelection: (params) => !params?.data?.isSuperAdmin,
      suppressMenu: true,
      wrapText: true,
      autoHeight: true, field: 'name',
      headerComponent: myComponent,
      key: 'name',
      suppressMenu: false,
      cellRenderer: (props) => {
        return <Typography variant='caption' display='block' fontWeight='regular' sx={{ cursor: 'pointer' }}>{props?.data?.name || '-'}</Typography>;
      },
      flex: 1
    },
    {
      headerName: 'Email',
      field: 'email',
      suppressMenu: false,
      minWidth: 250,
      flex: 1.5,
    },

    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      cellRenderer: (props) => {
        return <StatusText status={props?.data?.status} />;
      },
      minWidth: 100,
      maxWidth: 120,
      flex: 1
    },

    {
      headerName: 'Joined Date',
      field: 'joinedDate',
      suppressMenu: false,
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Roles',
      field: 'userRoles',
      suppressMenu: false,
      cellRenderer: (props) => {
        return props?.value?.length > 0 ? <Tooltip title={props?.value?.join(', ')} arrow placement='top'><Box mr={2} className='text-truncate'>{props?.value?.join(', ')}</Box></Tooltip> : '-';
      },
      minWidth: 140,
      flex: 1.25
    },
    {
      headerName: 'Actions',
      cellRenderer: (props) => {
        return <>
          <IconButton disabled={!canEdit} onClick={(e) => {
            e.stopPropagation();
            onEditClicked(props);
          }} color='primary' size='small' variant='primary'>
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton disabled={!canDelete || props?.data?.isSuperAdmin} onClick={(e) => {
            e.stopPropagation();
            dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props.data?._id }, modal: 'DELETE_MODAL' }));
          }} color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}>
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </>;
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      minWidth: 100,
      maxWidth: 100,
      flex: 1
    },

  ];

  function getSortInfo(data) {
    const result = {};
    data.forEach(item => {
      if (item?.colId === 'memberType') {
        result[item.colId] = item.sort === 'asc' ? 'AdultUse' : 'MedicinalUser';
      } else {
        result[item.colId] = item.sort === 'asc';
      }

    });
    return result;
  }

  const getRows = async (params) => {
    gridRefApi.current = params;
    let filter = {};
    if (params?.sortModel.length) {
      filter = { ...getSortInfo(params?.sortModel) };
    }
    dispatch(fetchRoleList({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current, ...filter })).unwrap().then(res => {
      const { values, total } = res;
      const adminArray = values?.map((item => {
        const date = new Date(item?.createdDate);
        const isSuperAdmin = Array.isArray(item?.userRoles) && item.userRoles.includes('Super Admin');
        return { ...item, status: item?.status || '-', joinedDate: formatDate(date), id: item._id, isSuperAdmin };
      }));

      params.successCallback([...adminArray], total);
      pageRef.current = pageRef.current + 20;
    }).catch(() => {
      params.successCallback([], 0);
    });
  };

  const searchFn = () => {
    const dataSource = {
      getRows
    };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption('datasource', dataSource);
  };

  const onEditClicked = (row) => {
    navigate(`/roles-permissions/add-user/${row?.data?._id}`);
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (roles?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      }
      else {
        mainGridApiRef.current.hideOverlay();
      }
    }

  }, [roles, loading]);

  const handleSearch = useDebounce(() => {
    // Perform search operation with the debounced term
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
    // Debounce the search callback
  };

  const onSelectionChanged = () => {
    const selectedRows = mainGridApiRef.current.getSelectedRows();
    const allRows = [];
    mainGridApiRef.current.forEachNode(node => {
      if (!node?.data?.isSuperAdmin) {
        allRows.push(node.data);
      }
    });

    // Check if all non-Super Admin rows are selected
    const allNonSuperAdminSelected = allRows.length > 0 &&
      selectedRows.length === allRows.length &&
      allRows.every(row => selectedRows.some(selected => selected._id === row._id));

    setSelectAllChecked(allNonSuperAdminSelected);

    if (selectedRows?.length > 0) {
      setShowBulkUpdate(true);
    } else {
      setShowBulkUpdate(false);
    }
  };
  const resetData = () => {
    pageRef.current = 0;
    setSelectAllChecked(false);
    if (mainGridApiRef.current) {
      mainGridApiRef.current.forEachNode((node) => {
        node.setSelected(false);
      });
    }
    searchFn();
  };
  const handleBulkDelete = () => {
    const selectedRows = mainGridApiRef.current.getSelectedRows();
    const filtered = selectedRows?.filter(row => !row?.isSuperAdmin) || [];
    const selectedIds = filtered.map(row => row._id);
    dispatch(openCustomModalDialog({
      modalData: {
        bulkProductIds: selectedIds, resetData,
        roletype: 'role'
      }, modal: 'BULK_DELETE_PRODUCT'
    }));
    // dispatch(deleteBulkRole(selectedIds)).unwrap().then(() => {
    //   dispatch(openSnackbar({ content: 'Roles deleted successfully', color: 'success', icon: 'check_circle' }));
    //   searchFn();
    // }).catch(() => {
    //   dispatch(openSnackbar({ content: 'Failed to delete roles', color: 'error', icon: 'warning' }));
    // });
  };

  return (
    <Box>
      <Grid container alignItems='center' spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Typography variant="h5" color="#9A9AAF">Users</Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack direction='row' justifyContent={{ xs: 'start', sm: 'end' }} flexWrap='wrap' alignItems='stretch' gap={2} className='search-grey-border'>
            <TextField
              className='custom-search-field'
              placeholder='Search'
              label=''
              value={searchTerm}
              onChange={(e) => searchHandler(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position='start'>
                  <IconButton size='small'>
                    <SearchIcon width='18px' height='18px' />
                  </IconButton>
                </InputAdornment>,
                endAdornment: searchTerm?.length > 0 ? <InputAdornment>
                  <IconButton onClick={() => searchHandler('')} size='small'>
                    <CloseRounded />
                  </IconButton>
                </InputAdornment> : null
              }}
              style={{ minWidth: "300px" }}
            />
            {
              canCreate &&
              <MDButton
                startIcon={<AddRoundedIcon fontSize='large' />}
                variant='contained' color='primary'
                onClick={() => navigate('/roles-permissions/add-user')} >
                Add User
              </MDButton>
            }
            {
              canDelete &&
              <MDButton
                style={{ minWidth: "180px" }}
                disabled={!showBulkUpdate}
                variant='contained'
                color='warning'
                onClick={handleBulkDelete}
              >Bulk Delete</MDButton>
            }
          </Stack>
        </Grid>
      </Grid>
      {
        canView &&
        <AgGridTable onSelectionChanged={onSelectionChanged}
          col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh', border: "1px solid #F9F9FA", BorderRadius: "8px" }}
        />
      }

    </Box>
  );
}